import { IStatsOutputPlugin } from "../statsCollectors"
import { manager, Subject } from ".."
import { IEBManagerConnector } from "../managerConnector"
import { Subscription, EMPTY, GroupedObservable, from, interval, empty, merge} from "rxjs"
import { filter, throttleTime, groupBy, mergeMap, bufferCount, map, take, bufferTime, delay} from "rxjs/operators"
import { logerr, loginfo, logwarn } from "../utils"

const EVENTCLASS = manager.ArgumentsEvent.EventClass
const ACTIONS = manager.ArgumentsEvent.Actions
const modName = "manager-out"
export class ManagerOutputPlugin implements IStatsOutputPlugin {
  public libVersion: string = "notset"
  private mc: IEBManagerConnector
  private eventSending: Subject<manager.Message> = new Subject<manager.Message>()
  private subscription: Subscription
  private subscriptionInterval: Subscription
  private peerState: manager.PeerState = new manager.PeerState()
  private peerStateFinal: manager.PeerState = new manager.PeerState()
  private peerStateInterval: number = 10000
  private peerStateCount: number[] = [0.01, 0.01, 0.01]

  // @todo: the lib information
  private libInfo: manager.Message = new manager.Message({
    Type: manager.MessageType.MessageTypeEvent,
    ArgumentsEvent: {
      EventName: EVENTCLASS.LIB,
      Action: ACTIONS.CONF,
      ArgumentsConf: {
        // replace with the version
      },
    },
  })
  public setLibVersion = (set: string): ManagerOutputPlugin => {
    this.libVersion = set
    return this
  }
  public bindManagerConnector = (mc: IEBManagerConnector): ManagerOutputPlugin => {
    this.mc = mc
    return this
  }

  public start = (): ManagerOutputPlugin => {
    if (!this.mc) {
      console.error(modName, "The managerConnector is not bound")
      return this
    }
    if (this.subscriptionInterval) {
      this.subscriptionInterval.unsubscribe()
    }
    // this is the event that's sent at an interval
    this.subscriptionInterval = merge(
      interval(this.peerStateInterval).pipe(
        map((seq) => {
          const ps = new manager.PeerState({
            FreeSlot: Number((this.peerState.FreeSlot / this.peerStateCount[0]).toFixed(3)),
            OccupiedSlot: Number((this.peerState.OccupiedSlot / this.peerStateCount[0]).toFixed(3)),
            WatchingTimeSec: this.peerState.WatchingTimeSec,
            BufferLengthBytes: Number((this.peerState.BufferLengthBytes / this.peerStateCount[1]).toFixed(3)),
            SwarmSize: Number((this.peerState.SwarmSize / this.peerStateCount[2]).toFixed(3)),
          })
          this.peerState = ps
          return new manager.Message({
            Type: 1025,
            Timestamp: Date.now(),
            ArgumentsEvent: {
              EventName: EVENTCLASS.PEER,
              Action: ACTIONS.STATE,
              ArgumentsPeerState: ps,
            },
          })
        }),
      ),
    ).subscribe({
      next: (msg) => {
        loginfo(modName, `${JSON.stringify(msg.toJSON())}`)
        this.mc.next(msg)
        this.peerStateCount = [1, 1, 1]
        this.peerState.SwarmSize = this.peerStateFinal.SwarmSize
        this.peerState.FreeSlot = this.peerStateFinal.FreeSlot
        this.peerState.OccupiedSlot = this.peerStateFinal.OccupiedSlot
      },
    })
    if (this.subscription) {
      this.subscription.unsubscribe()
    }

    this.subscription = this.eventSending.pipe(
      filter((value) => value.Type === manager.MessageType.MessageTypeEvent),
      groupBy((value) => value.ArgumentsEvent.EventName),
      mergeMap((group$) => {
        // the group resource downloaded
        switch (group$.key) {
          case EVENTCLASS.RESOURCE:
            const pipeExchange = group$.pipe(
              map((value) => {
                if (value.ArgumentsEvent.Action === ACTIONS.SLOTUPDATE) {
                  const su = value.ArgumentsEvent.ArgumentsSlotUpdate
                  this.peerStateCount[0] += 1
                  this.peerState.OccupiedSlot += su.OccupiedSlot
                  this.peerStateFinal.OccupiedSlot = su.OccupiedSlot
                  this.peerState.FreeSlot += su.FreeSlot
                  this.peerStateFinal.FreeSlot = su.FreeSlot
                }
                return value
              }),
              filter((value) => value.ArgumentsEvent.Action === ACTIONS.DOWNLOADED ||
                value.ArgumentsEvent.Action === ACTIONS.UPLOADED),
              bufferTime(10000),
              filter((value) => value.length !== 0),
              map((value) => {
                return new manager.Message({
                  Type: 1025,
                  ArgumentsEvent: {
                    EventName: EVENTCLASS.RESOURCE,
                    Action: ACTIONS.EXCHANGE,
                    ArgumentsResourceExchanges: {
                      Lists: value.map((msg) => {
                        const res = msg.ArgumentsEvent.ArgumentsResourceDownloaded ||
                        msg.ArgumentsEvent.ArgumentsResourceUploaded
                        res.Pid = msg.PeerID
                        res.Pid2 = msg.ArgumentsEvent.RemotePeerID
                        return res
                      }),
                    },
                  },
                })
              }))
            const pipeAvailable = group$.pipe(filter((value) => value.ArgumentsEvent.Action === ACTIONS.AVAILABILITY))
            return merge(pipeExchange, pipeAvailable)
          case EVENTCLASS.PEER:
            return group$.pipe(
              map((value) => {
                if (value.ArgumentsEvent.Action === ACTIONS.STATE) {
                  try {
                    const bb = (this.peerState.BufferLengthBytes as number) +
                    (value.ArgumentsEvent.ArgumentsPeerState.BufferLengthBytes as number)
                    this.peerState.BufferLengthBytes = bb
                    this.peerState.WatchingTimeSec = value.ArgumentsEvent.ArgumentsPeerState.WatchingTimeSec
                    this.peerStateCount[1] ++
                  } catch (err) { logwarn(modName, "The peer state is sent erronously", err, value) }
                }
                return value
              }),
              filter((value) => value.ArgumentsEvent.Action === ACTIONS.INFO),
              groupBy((msg) => msg.ArgumentsEvent.Action),
              mergeMap((group$$) => {
                switch (group$$.key) {
                case ACTIONS.INFO:
                  return group$$.pipe(
                    throttleTime(10000),
                    map((value) => {
                      value.ArgumentsEvent.ArgumentsPeerInfo.Version = this.libVersion
                      return value
                    }),
                  )
                  break
                }
              }),
            )
          case EVENTCLASS.PINGPONG:
            // REMOVED P2P Stats
            return group$
          case EVENTCLASS.NETWORK:
            return group$.pipe(delay(1000))
          case EVENTCLASS.PLAYER:
            return group$.pipe(
              filter((value) => value.ArgumentsEvent.Action === ACTIONS.STATE),
              throttleTime(10000),
            )
          case EVENTCLASS.SWARM:
            group$.subscribe({
              next: (evt) => {
                if (evt.ArgumentsEvent.Action !== ACTIONS.SIZEUPDATE) {
                  return
                }
                const size = evt.ArgumentsEvent.ArgumentsSwarmSizeUpdate.Size
                this.peerState.SwarmSize += size
                this.peerStateFinal.SwarmSize = size
                this.peerStateCount[2] += 1
              },
            })
          default:
            return EMPTY
        }
      }),
    ).subscribe({
      next: (msg) => {
        loginfo(modName, `${JSON.stringify(msg.toJSON())}`)
        this.mc.next(msg)
      },
    })
    return this
  }

  public stop = (): ManagerOutputPlugin => {
    if (!this.subscription) { return }
    this.subscription.unsubscribe()
    return this
  }

  public next = (msg: manager.Message) => {
    this.eventSending.next(msg)
  }

}
